import React from 'react';
import clsx from 'clsx';
import { CalendarIcon } from '@heroicons/react/16/solid';
import { ViolationReturn } from '../../clients/rust-api-client';
import {
  fineStatusClasses,
  fineStatusLabel,
  formatBillingDate,
  formatMoney,
} from '../../utils/billingHelpers';
import { SeverityGlyph, StatusIcon } from './StatusIcon';
import {
  formatDay,
  isOverdue,
  normalizeSeverity,
  statusLabel,
} from './violationMeta';

/**
 * The homeowner's Properties panel (#603 step 2): the same layout
 * managers see, read-only. A homeowner changes nothing about a violation
 * from here — status and fines are management actions — so there are no
 * pickers to render as disabled, only facts.
 */
const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <section className="py-3 first:pt-0">
    <h2 className="mb-1.5 px-2 text-[12px] font-medium text-fg-muted">
      {title}
    </h2>
    <div className="flex flex-col gap-0.5">{children}</div>
  </section>
);

const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex w-full items-baseline justify-between gap-3 px-2 py-1 text-[13px]">
    <span className="text-fg-muted">{label}</span>
    <span className="text-right text-fg-secondary">{children}</span>
  </div>
);

const Line: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex items-center gap-1.5 px-2 py-1 text-[13px] text-fg-secondary">
    {children}
  </div>
);

export const MemberViolationProperties: React.FC<{ violation: ViolationReturn }> = ({
  violation: v,
}) => {
  const overdue = isOverdue(v);
  const severity = normalizeSeverity(v.severity);
  return (
    <div className="divide-y divide-line/60">
      <Section title="Properties">
        <Line>
          <StatusIcon status={v.status} />
          {statusLabel(v.status)}
        </Line>
        <Line>
          <SeverityGlyph severity={severity} />
          {severity}
        </Line>
        <Line>
          <CalendarIcon
            className={clsx('h-3.5 w-3.5', overdue ? 'text-red-400' : 'text-zinc-400')}
            aria-hidden
          />
          <span className={clsx(overdue && 'text-red-400')}>
            {v.due_date ? `Due ${formatDay(v.due_date)}` : 'No due date'}
            {overdue && <span className="sr-only"> (overdue)</span>}
          </span>
        </Line>
      </Section>

      {/* A fine is an ordinary charge on the homeowner's ledger, so what is
          owed and what has been paid come from the same place as everything
          else they owe. Amounts are decimal strings — formatMoney, never
          toFixed. */}
      <Section title="Fine">
        {v.fine ? (
          <>
            <Fact label="Amount">
              <span className="inline-flex items-center gap-2">
                {formatMoney(v.fine.amount)}
                <span
                  className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${fineStatusClasses(
                    v.fine.status
                  )}`}
                >
                  {fineStatusLabel(v.fine.status)}
                </span>
              </span>
            </Fact>
            <Fact label="Paid">{formatMoney(v.fine.amount_paid)}</Fact>
            <Fact label="Outstanding">{formatMoney(v.fine.amount_outstanding)}</Fact>
            {v.fine.due_date && (
              <Fact label="Due">{formatBillingDate(v.fine.due_date)}</Fact>
            )}
          </>
        ) : (
          <p className="px-2 py-1 text-[13px] text-fg-muted">
            This violation carries no fine.
          </p>
        )}
      </Section>

      <Section title="Details">
        <Fact label="Type">{v.violation_type}</Fact>
        <Fact label="Observed">{formatDay(v.date_of_violation) || '—'}</Fact>
        {v.resolution_date && <Fact label="Resolved">{formatDay(v.resolution_date)}</Fact>}
      </Section>
    </div>
  );
};
